import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { CDNAssets } from '../CDN';
import ScrollParallax from '../components/interactive/ScrollParallax';
import AlternateLayoutList from '../components/layout/lists/AlternateLayoutList';
import ParallaxLayoutList, { ParallaxLayoutTextDecsriptionCard } from '../components/layout/lists/ParallaxLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import PageSectionTitle from '../components/layout/sections/PageSectionTitle';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import FAQWidget from '../widgets/interactive/FAQWidget';

// Dynamically import with SSR disabled
const InstallationGuideShowcase = dynamic(() => import('../widgets/showcases/InstallationGuideShowcase'), { ssr: false });

type PortalDocumentationPageStaticProps = {};

export const getStaticProps: GetStaticProps<PortalDocumentationPageStaticProps> = async (context) => {
  const props: PortalDocumentationPageStaticProps = {};
  return withGlobalDefaultStaticProps(context, { props });
};

const PortalDocumentationPage: NextPageWithLayout<PortalDocumentationPageStaticProps> = (props) => {
  const seoConfig = getPageSeoConfig(pageRoutes.PortalDocumentation);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Portal Documentation"
        title="Business insights and data mastery"
        description="Unlock the full potential of Gaspar AI with our Admin Hub - where customization meets simplicity and intelligence! One single interface to review everything support-related, get smart business insights and optimize knowledge management."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Transform your HR helpdesk with AI capabilities" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('portal-page-take-control-of-AI'),
              orientation: 'left',
              title: 'Take control of AI',
              description:
                'The Admin Hub is the administrative interface to the intelligence behind Gaspar AI. Seamlessly integrate and tailor Gaspar AI to suit your enterprise needs with our intuitive, no-code interface.',
            },
            {
              imageUrl: CDNAssets.getUrl('portal-page-unleash-business-intelligence'),
              orientation: 'right',
              title: 'Unleash business intelligence',
              description:
                'Drill down into the most impactful support issues, measure the success of your AI platform, get recommendations on process improvements and identify automation opportunities.',
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <ScrollParallax
          title="Your Gaspar AI experience on a single screen"
          panels={[
            {
              title: 'Dashboard',
              description: 'Assess your AI helpdesk’s impact by analyzing performance metrics and reviewing statistics',
              imageUrl: CDNAssets.getUrl('portal-page-dashboard'),
            },
            {
              title: 'User management',
              description: 'Manage user permissions and access to the platform and its capabilities.',
              imageUrl: CDNAssets.getUrl('portal-page-user-management'),
            },
            {
              title: 'Customization',
              description: 'Fully customise your experience! Choose third party integrations and automations.',
              imageUrl: CDNAssets.getUrl('portal-page-customisation'),
            },
            {
              title: 'Employee insights',
              description: 'Gaspar AI uncovers productivity blockers. Review the main issues employees face',
              imageUrl: CDNAssets.getUrl('portal-page-employee-insights'),
            },
            {
              title: 'Recommendations',
              description: 'Identify high-impact automation, content, and process improvements at the click of a button.',
              imageUrl: CDNAssets.getUrl('portal-page-recommendations'),
            },
            {
              title: 'Reporting',
              description: 'Summarize the most important charts and data for your leadership team.',
              imageUrl: CDNAssets.getUrl('portal-page-reporting'),
            },
            {
              title: 'Benchmark',
              description: 'Compare service performance over time and take action when needed.',
              imageUrl: CDNAssets.getUrl('portal-page-benchmark'),
            },
            {
              title: 'Workflows',
              description: 'Create custom workflows to automate repetitive processes.',
              imageUrl: CDNAssets.getUrl('portal-page-workflows'),
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle mode="parallax" text="How to get started" />
        <ParallaxLayoutList
          renderItem={(item) => <ParallaxLayoutTextDecsriptionCard {...item} />}
          items={[
            {
              title: 'Single Sign-On (SSO) Provider',
              data: {
                description:
                  'Integrate your preferred Single Sign-On (SSO) provider, such as Google Workspace or Office 365. This ensures heightened security and simplifies user access, laying the foundation for a streamlined experience.',
              },
            },
            {
              title: 'Messaging Platform Integration',
              data: {
                description:
                  'Offer efficient support where employees are by integrating Gaspar AI with your company’s messaging platform, be it Slack or Microsoft Teams. Gaspar seamlessly interacts with end-users, providing instant support, updates, and a direct channel for streamlined workflows.',
              },
            },
            {
              title: 'ITSM - Ticketing Tool Integration',
              data: {
                description:
                  "Integrate Gaspar AI with your company’s IT Service Management (ITSM) tool—whether it's Jira SM, ServiceNow, Freshservice, Ivanti or other. This ensures a cohesive workflow, enabling easy ticket creation, management, and resolution, all within a unified system.",
              },
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <InstallationGuideShowcase
          hasGradientEdge={true}
          description={`Follow these simple steps to install Gaspar AI and boost your company's efficiency and productivity.`}
          records={[
            { title: 'Start free trial', description: 'by filling in the form below' },
            { title: 'Log in with your SSO', description: 'ex Google, Microsoft 365' },
            { title: 'Enable the SSO', description: 'on the Admin Hub/Integrations' },
            { title: 'Enable and configure ', description: 'the ITSM on the Admin Hub/Integrations' },
            {
              title: 'Enable messaging tool',
              description: 'e.g. Slack, MS Teams, etc. on the Admin Hub/Integrations',
            },
            { title: 'Invite Gaspar', description: 'Invite and add our AI chatbot to the channels you want' },
            { title: 'Choose channel settings ', description: 'Choose when Gaspar will respond to messages.' },
            { title: 'Choose Emojis', description: 'Choose which emojis to use to manage tickets' },
            { title: 'Enable automations', description: 'Select the automations you wish to have' },
          ]}
        />
      </PageSection>
      <PageSection>
        <FAQWidget />
      </PageSection>
    </>
  );
};
PortalDocumentationPage.displayName = 'PortalDocumentationPage';
export default PortalDocumentationPage;
